import { useEffect, useRef, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/map";
import proj4 from "proj4";
import mapData from "@highcharts/map-collection/countries/us/us-all.geo.json";
import { getTierStyle } from "src/charts/types";
import type { QuadrantPractice } from "src/charts/types";

// proj4 must be on window AND referenced in chart.proj4 so Highcharts can
// project lat/lon via the hc-transform embedded in us-all.geo.json
if (typeof window !== "undefined") {
  (window as any).proj4 = proj4;
}

// US state centre coordinates (WGS84)
const STATE_COORDS: Record<string, { lat: number; lon: number }> = {
  AL: { lat: 32.7, lon: -86.7 },
  AK: { lat: 64.2, lon: -153.4 },
  AZ: { lat: 34.3, lon: -111.1 },
  AR: { lat: 34.8, lon: -92.2 },
  CA: { lat: 36.8, lon: -119.4 },
  CO: { lat: 39.0, lon: -105.5 },
  CT: { lat: 41.6, lon: -72.7 },
  DE: { lat: 39.0, lon: -75.5 },
  FL: { lat: 27.8, lon: -81.6 },
  GA: { lat: 32.2, lon: -82.9 },
  HI: { lat: 20.8, lon: -156.3 },
  ID: { lat: 44.4, lon: -114.5 },
  IL: { lat: 40.0, lon: -89.2 },
  IN: { lat: 39.8, lon: -86.1 },
  IA: { lat: 42.1, lon: -93.5 },
  KS: { lat: 38.5, lon: -98.4 },
  KY: { lat: 37.7, lon: -84.9 },
  LA: { lat: 31.2, lon: -91.8 },
  ME: { lat: 45.3, lon: -69.2 },
  MD: { lat: 39.0, lon: -76.8 },
  MA: { lat: 42.2, lon: -71.5 },
  MI: { lat: 43.3, lon: -84.5 },
  MN: { lat: 46.4, lon: -93.1 },
  MS: { lat: 32.7, lon: -89.7 },
  MO: { lat: 38.5, lon: -92.3 },
  MT: { lat: 47.0, lon: -110.0 },
  NE: { lat: 41.5, lon: -99.9 },
  NV: { lat: 38.5, lon: -117.1 },
  NH: { lat: 43.7, lon: -71.6 },
  NJ: { lat: 40.1, lon: -74.7 },
  NM: { lat: 34.5, lon: -106.2 },
  NY: { lat: 42.9, lon: -75.5 },
  NC: { lat: 35.5, lon: -79.4 },
  ND: { lat: 47.5, lon: -100.5 },
  OH: { lat: 40.4, lon: -82.8 },
  OK: { lat: 35.6, lon: -96.9 },
  OR: { lat: 44.1, lon: -120.5 },
  PA: { lat: 40.9, lon: -77.8 },
  RI: { lat: 41.7, lon: -71.5 },
  SC: { lat: 33.9, lon: -80.9 },
  SD: { lat: 44.4, lon: -100.2 },
  TN: { lat: 35.9, lon: -86.7 },
  TX: { lat: 31.0, lon: -100.0 },
  UT: { lat: 39.4, lon: -111.1 },
  VT: { lat: 44.1, lon: -72.7 },
  VA: { lat: 37.8, lon: -79.5 },
  WA: { lat: 47.4, lon: -120.5 },
  WV: { lat: 38.6, lon: -80.5 },
  WI: { lat: 44.3, lon: -89.6 },
  WY: { lat: 43.0, lon: -107.6 },
  DC: { lat: 38.9, lon: -77.0 },
};

function jitter(seed: number, range: number): number {
  const h = ((seed ^ (seed >> 16)) * 0x45d9f3b) & 0x7fffffff;
  return ((h % 1000) / 1000 - 0.5) * range * 2;
}

type Props = {
  practices: QuadrantPractice[];
  selectedPractice: QuadrantPractice | null;
  chartId: string;
  onPracticeSelect?: (id: number | string | null) => void;
};

export default function PracticeMapWidget({
  practices,
  selectedPractice,
  chartId,
  onPracticeSelect,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // The click handler lives in a ref so the chart options never depend on it —
  // rebuilding options triggers chart.update(), which races with the zoom
  // animation (same reason selectedPractice is kept out of pointData's deps).
  const onSelectRef = useRef(onPracticeSelect);
  onSelectRef.current = onPracticeSelect;

  // Read at hover time by the tooltip formatter — a ref keeps the selected id
  // fresh without putting it in the options deps.
  const selectedKeyRef = useRef<number | string | null>(null);
  selectedKeyRef.current =
    selectedPractice?.practice_id ?? selectedPractice?.practice ?? null;

  // Build lat/lon point data. NOTE: selectedPractice intentionally NOT in deps —
  // including it would trigger chart.update() on every click which races with
  // the zoom effect and causes dots to disappear. Highlight is handled via SVG attr.
  const pointData = useMemo(
    () =>
      practices
        .filter((p) => p.location && STATE_COORDS[p.location])
        .map((p) => {
          const base = STATE_COORDS[p.location!]!;
          const id = Number(p.practice_id) || 0;
          const s = getTierStyle(p.tier);
          return {
            lat: base.lat + jitter(id, 0.35),
            lon: base.lon + jitter(id + 999, 0.35),
            color: s.dot,
            name: p.practice ?? "—",
            tier: p.tier ?? "—",
            pts: p.pts ?? 0,
            location: p.location ?? "—",
            custom: { id: p.practice_id ?? p.practice ?? null },
            marker: { radius: 5, lineWidth: 0, lineColor: "#fff" },
          };
        }),
    [practices],
  );

  // Zoom to selected state + highlight selected dot
  useEffect(() => {
    const chart = (chartRef.current as any)?.chart;
    const mapView = chart?.mapView;
    if (!mapView) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const selectedKey =
      selectedPractice?.practice_id ?? selectedPractice?.practice ?? null;

    // pt.graphic renders as a <path>, not <circle> — SVG `r` attr does nothing on paths.
    // CSS transform on the raw DOM element is never touched by Highcharts so it
    // survives every redraw and zoom-animation frame.
    const applyHighlight = () => {
      const pts: any[] = chart.series?.[1]?.points ?? [];
      pts.forEach((pt: any) => {
        const el = pt.graphic?.element as SVGElement | undefined;
        if (!el) return;
        const isSelected =
          selectedKey !== null && pt.options?.custom?.id === selectedKey;
        const hasSelection = selectedKey !== null;
        (el.style as any).transformBox = "fill-box";
        el.style.transformOrigin = "center";
        el.style.transform = isSelected ? "scale(2.2)" : "scale(1)";
        el.style.opacity = hasSelection && !isSelected ? "0.25" : "1";
        el.style.stroke = isSelected ? "#FFFFFF" : "none";
        el.style.strokeWidth = isSelected ? "1.5" : "0";
        el.style.transition = reduceMotion
          ? "opacity 0.15s ease"
          : "transform 0.18s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.18s ease";
        // Selected dot renders above its siblings.
        if (isSelected) el.parentNode?.appendChild(el);
      });
    };

    const zoomAnimation = reduceMotion ? false : undefined;

    if (!selectedPractice?.location) {
      mapView.fitToBounds?.(undefined, undefined, true, zoomAnimation);
      requestAnimationFrame(applyHighlight);
      return;
    }

    // Prefer the real projected bounds of the state polygon (frames TX and DE
    // equally well); fall back to a fixed box around the state centroid.
    const statePoints: any[] = chart.series?.[0]?.points ?? [];
    const statePoint = statePoints.find(
      (pt: any) =>
        pt.properties?.["postal-code"] === selectedPractice.location,
    );
    let bounds = statePoint?.bounds ? { ...statePoint.bounds } : null;

    if (bounds) {
      // Small states (DE, RI…) zoom in so deep there's no geographic context.
      // Enforce a minimum extent relative to the continental map so the
      // selected state always appears within its region. AK/HI live in
      // projection insets, so they're excluded from the full-extent calc.
      let fx1 = Infinity, fy1 = Infinity, fx2 = -Infinity, fy2 = -Infinity;
      statePoints.forEach((pt: any) => {
        const code = pt.properties?.["postal-code"];
        if (code === "AK" || code === "HI") return;
        const b = pt.bounds;
        if (!b) return;
        fx1 = Math.min(fx1, b.x1);
        fy1 = Math.min(fy1, b.y1);
        fx2 = Math.max(fx2, b.x2);
        fy2 = Math.max(fy2, b.y2);
      });
      if (Number.isFinite(fx1)) {
        const minW = (fx2 - fx1) / 4.5;
        const minH = (fy2 - fy1) / 4.5;
        const cx = (bounds.x1 + bounds.x2) / 2;
        const cy = (bounds.y1 + bounds.y2) / 2;
        const w = Math.max(bounds.x2 - bounds.x1, minW);
        const h = Math.max(bounds.y2 - bounds.y1, minH);
        bounds = { x1: cx - w / 2, x2: cx + w / 2, y1: cy - h / 2, y2: cy + h / 2 };
      }
      mapView.fitToBounds(bounds, 28, true, zoomAnimation);
    } else {
      const coords = STATE_COORDS[selectedPractice.location];
      if (coords) {
        const D = 4;
        const corners = [
          { lat: coords.lat + D, lon: coords.lon - D },
          { lat: coords.lat - D, lon: coords.lon + D },
        ]
          .map((c) => mapView.lonLatToProjectedUnits?.(c))
          .filter(Boolean) as { x: number; y: number }[];
        if (corners.length === 2) {
          const [a, b] = corners as [
            { x: number; y: number },
            { x: number; y: number },
          ];
          mapView.fitToBounds(
            {
              x1: Math.min(a.x, b.x),
              x2: Math.max(a.x, b.x),
              y1: Math.min(a.y, b.y),
              y2: Math.max(a.y, b.y),
            },
            undefined,
            true,
            zoomAnimation,
          );
        }
      }
    }

    // Apply after synchronous redraw, then again after zoom animation (400ms) ends
    requestAnimationFrame(applyHighlight);
    const timer = setTimeout(applyHighlight, 450);
    return () => clearTimeout(timer);
  }, [selectedPractice]);

  // Reflow on container resize
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const reflow = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        (chartRef.current as any)?.chart?.reflow(),
      );
    };
    const timer = setTimeout(reflow, 0);
    const ro = new ResizeObserver(reflow);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        // Pass proj4 explicitly so Highcharts can resolve hc-transform CRS strings
        proj4,
        animation: { duration: 400 },
        style: { fontFamily: "inherit" },
        events: {
          // Clicking anywhere that isn't a practice dot clears the selection,
          // mirroring the scatter's background-click behavior. Dot clicks are
          // captured by the point tracker and never reach this handler, and
          // Highcharts suppresses the click after a pan drag (cancelClick).
          click: function (e: any) {
            // The zoom +/- buttons sit inside the plot area; zooming must not
            // drop the selection.
            const target = e?.target as Element | undefined;
            if (target?.closest?.(".highcharts-map-navigation")) return;
            onSelectRef.current?.(null);
          },
        },
      } as any,
      title: { text: "" },
      credits: { enabled: false },
      legend: { enabled: false },
      exporting: { enabled: false },
      mapNavigation: {
        enabled: true,
        enableDoubleClickZoomTo: true,
        buttonOptions: { verticalAlign: "bottom", align: "right" },
      },
      tooltip: {
        useHTML: true,
        formatter(this: any) {
          if (!this.point?.tier) return false;
          const s = getTierStyle(this.point.tier);
          return `<div style="padding:2px 2px;min-width:170px;">
            <div style="font-size:12px;font-weight:700;margin-bottom:4px;">${this.point.name}</div>
            <div style="display:flex;align-items:center;gap:5px;margin-bottom:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${s.dot}"></span>
              <span style="font-size:11px;color:${s.text};background:${s.bg};border:1px solid ${s.border};border-radius:10px;padding:0 6px">${this.point.tier}</span>
            </div>
            <div style="font-size:11px;">Cases: <b>${this.point.pts}</b></div>
            <div style="font-size:10px;color:#94a3b8;margin-top:4px;">State-level position · ${this.point.location}</div>
            <div style="font-size:10px;color:#94a3b8;margin-top:4px;">${
              this.point.options?.custom?.id === selectedKeyRef.current
                ? "Click again to clear selection"
                : "Click to see prescribers"
            }</div>
          </div>`;
        },
      },
      series: [
        {
          type: "map",
          mapData,
          nullColor: "#E8EAED",
          borderColor: "#C8CDD4",
          borderWidth: 0.5,
          enableMouseTracking: false,
          dataLabels: { enabled: false },
        } as any,
        {
          type: "mappoint",
          name: "Practices",
          data: pointData,
          dataLabels: { enabled: false },
          marker: { symbol: "circle" },
          cursor: "pointer",
          stickyTracking: false,
          point: {
            events: {
              click: function (this: any) {
                onSelectRef.current?.(this.options?.custom?.id ?? null);
              },
            },
          },
        } as any,
      ],
    }),
    [pointData],
  );

  return (
    <div ref={wrapperRef} style={{ width: "100%", height: "100%" }}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        constructorType="mapChart"
        options={options}
        containerProps={{
          id: chartId,
          style: { width: "100%", height: "100%" },
        }}
      />
    </div>
  );
}
